import { Alert, CircularProgress, Container, FormControlLabel, InputLabel, MenuItem, Select } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { CloudUpload, CloudUploadOutlined } from "@mui/icons-material";
import React, { useCallback, useState } from "react";
import {
  Footer,
  FormComponentContainer,
  FormContainer,
  StyledAvatar,
  StyledFormControl,
  StyledInput,
  StyledLabel,
  StyledSwitch,
} from "components/pages/IndexPage.styles";
import { useSnackbar } from "notistack";
import {
  DETAILED_ERROR_SNACKBAR_OPTIONS,
  SUCCESS_SNACKBAR_OPTIONS,
  WARNING_SNACKBAR_OPTIONS,
} from "utils/notifications";
import { uploadFile, UploadFileResponse } from "service/fileupload.service";
import { FileType, FileTypeContext, getAllFileTypes, getFileTypeConfiguration } from "config/FileTypeConfiguration";
import { LogoLinkContainer } from "components/core/logo/LogoLinkContainer";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";

type FormState = {
  configurationId?: string;
  authorisationToken?: string;
  fileType?: FileType;
  fileToUpload?: File;
};

export const IndexPage = (): JSX.Element => {
  const [formState, setFormState] = useState<FormState>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectedFileTypeContext, setSelectedFileTypeContext] = useState<FileTypeContext>();
  const [withLoosenedValidation, setLoosenedValidationFlag] = useState<boolean>(false);

  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const getErrorMessage = useCallback((error: UploadFileResponse) => {
    if (!error) {
      return "An error has occurred while uploading the document.";
    }

    return error.error?.message;
  }, []);

  const setFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      setFormState((formState) => ({ ...formState, fileToUpload: file }));
      event.target.value = "";
    }
  }, []);

  const setAuthorisationToken = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setFormState((formState) => ({ ...formState, authorisationToken: event.target.value }));
  }, []);

  const setConfigurationId = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setFormState((formState) => ({ ...formState, configurationId: event.target.value }));
  }, []);

  const setFileType = useCallback((event: SelectChangeEvent<any>) => {
    const value = event.target.value as FileType;

    if (value) {
      setFormState((formState) => ({ ...formState, fileType: event.target.value as FileType }));
      const fileTypeConfiguration = getFileTypeConfiguration(value);
      setSelectedFileTypeContext(fileTypeConfiguration);
      setLoosenedValidationFlag(false);
    }
  }, []);

  const handleUpload = useCallback(async () => {
    closeSnackbar();

    if (!formState.configurationId || !formState.authorisationToken || !formState.fileType) {
      enqueueSnackbar(
        "Please complete the form. Required fields: configuration id, authorisation token and file type.",
        WARNING_SNACKBAR_OPTIONS
      );

      return;
    }

    const formData = new FormData();

    if (formState.fileToUpload) {
      formData.append("file", formState.fileToUpload);
    } else {
      enqueueSnackbar(
        "File for the upload not chosen. Please click on the button and attach the required file.",
        WARNING_SNACKBAR_OPTIONS
      );

      return;
    }

    setIsUploading(true);

    uploadFile(
      formData,
      formState.configurationId,
      formState.authorisationToken,
      formState.fileType,
      withLoosenedValidation
    )
      .then(() => {
        enqueueSnackbar(`File ${formState.fileToUpload?.name} successfully uploaded.`, SUCCESS_SNACKBAR_OPTIONS);
        setIsUploading(false);
        setLoosenedValidationFlag(false);
      })
      .catch((error) => {
        enqueueSnackbar(getErrorMessage(error), DETAILED_ERROR_SNACKBAR_OPTIONS(error.error.requestId));
        setIsUploading(false);
        setLoosenedValidationFlag(false);
      })
      .finally(() => {
        setFormState({
          configurationId: formState.configurationId,
          authorisationToken: formState.authorisationToken,
          fileType: formState.fileType,
          fileToUpload: undefined,
        });
      });
  }, [
    closeSnackbar,
    enqueueSnackbar,
    formState.authorisationToken,
    formState.configurationId,
    formState.fileToUpload,
    formState.fileType,
    getErrorMessage,
    withLoosenedValidation,
  ]);

  return (
    <Container component="main" maxWidth="sm">
      <FormContainer>
        <StyledAvatar>
          <CloudUploadOutlined />
        </StyledAvatar>
        <FormComponentContainer>
          <Typography component="h1" variant="h5">
            RedX File Upload
          </Typography>
        </FormComponentContainer>
        <form noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Configuration ID"
            name="username"
            onBlur={setConfigurationId}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            id="password"
            label="Auth Token"
            onBlur={setAuthorisationToken}
          />
          <StyledFormControl variant="outlined">
            <InputLabel>File type</InputLabel>
            <Select
              value={formState.fileType ? formState.fileType : ""}
              label={"File Type"}
              onChange={setFileType}
              inputProps={{
                name: "fileType",
              }}
            >
              {getAllFileTypes().map(([fileType, context], index) => (
                <MenuItem key={index} value={fileType.valueOf()}>
                  {context.description}
                </MenuItem>
              ))}
            </Select>
            {!!selectedFileTypeContext?.disableValidationConfig?.hasDisableValidationToggle && (
              <>
                <FormControlLabel
                  control={
                    <StyledSwitch
                      checked={withLoosenedValidation}
                      color="primary"
                      onChange={() => {
                        setLoosenedValidationFlag((prevState) => !prevState);
                      }}
                    />
                  }
                  label={selectedFileTypeContext?.disableValidationConfig.disableValidationLabel}
                />
                {withLoosenedValidation && (
                  <Alert severity="warning">
                    {selectedFileTypeContext?.disableValidationConfig.disableValidationWarning}
                  </Alert>
                )}
              </>
            )}
          </StyledFormControl>
          <FormComponentContainer>
            <StyledInput
              id="fileUpload"
              name="fileUpload"
              accept=".csv, .xls, .xlsx, .xlsm"
              capture="environment"
              type="file"
              onChange={setFile}
              hidden={true}
            />
            <StyledLabel htmlFor="fileUpload">
              <Button color="primary" component={"span"} variant={"outlined"} fullWidth={true}>
                <CloudUpload style={{ marginRight: "10px" }} />
                {formState.fileToUpload?.name !== undefined ? formState.fileToUpload?.name : "Select file to upload"}
              </Button>
            </StyledLabel>
          </FormComponentContainer>
          <Button variant="contained" color="primary" onClick={handleUpload} disabled={isUploading} fullWidth={true}>
            {isUploading ? <CircularProgress size={"24px"} /> : "upload"}
          </Button>
        </form>
      </FormContainer>
      <Box mt={14}>
        <Footer>
          <LogoLinkContainer href={"https://www.wuestpartner.com/"} text={"Powered by"} type={"WUP"} />
          <LogoLinkContainer href={"https://netcetera.com/"} text={"Developed by"} type={"NETCETERA"} />
        </Footer>
      </Box>
    </Container>
  );
};

export default IndexPage;
